import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { artisanProfileService, type ArtisanProfileFilters } from '../../../services/artisanProfileService';
import { workOrderService } from '../../../services/workOrderService';
import { usePaginationState } from '../../../hooks/usePaginationState';
import { invalidateArtisanLinkQueries } from '../../../lib/artisanLinkQueries';

/**
 * Axios rejects with an Error subclass, so the server payload has to be read before
 * `error.message` — otherwise it shadows it with "Request failed with status code N".
 */
export const getErrorMessage = (error: unknown, fallback: string) => {
    if (error && typeof error === 'object') {
        const maybeResponse = (error as { response?: { data?: { message?: string; error?: string } } }).response;
        if (maybeResponse?.data?.message) return maybeResponse.data.message;
        if (maybeResponse?.data?.error) return maybeResponse.data.error;
        const message = (error as { message?: string }).message;
        if (typeof message === 'string' && message) return message;
    }
    return fallback;
};

export const getErrorStatus = (error: unknown): number | undefined =>
    (error as { response?: { status?: number } } | undefined)?.response?.status;

/** Reads the profile id a 409 upload names, so a duplicate can still be opened from the results. */
export const getDuplicateProfileId = (error: unknown): string | undefined =>
    (error as { response?: { data?: { artisan_profile_id?: string } } } | undefined)
        ?.response?.data?.artisan_profile_id;

/** Reads the work order number a 409 delete names, so the UI can say which one blocks it. */
export const getBlockingWorkOrder = (error: unknown): string | undefined =>
    (error as { response?: { data?: { work_order_number?: string } } } | undefined)
        ?.response?.data?.work_order_number;

interface Options {
    isSyncModalOpen: boolean;
}

export function useArtisanSyncPage({ isSyncModalOpen }: Options) {
    const queryClient = useQueryClient();

    const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
    const [filters, setFilters] = useState<ArtisanProfileFilters>({ status: 'all' });

    const { page, size, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
    resetOnChange([filters.status, filters.roaster, filters.machine, filters.date_from, filters.date_to]);

    // ─── Queries ───
    const { data: profilesResponse, isLoading: isLoadingList } = useQuery({
        queryKey: ['artisan-profiles', { page, size, ...filters }],
        queryFn: () => artisanProfileService.list({ page, size, ...filters }),
        placeholderData: keepPreviousData,
        staleTime: 5 * 60 * 1000,
    });

    const { data: selectedProfile, isLoading: isLoadingDetail } = useQuery({
        queryKey: ['artisan-profiles', selectedProfileId],
        queryFn: () => artisanProfileService.getById(selectedProfileId!),
        enabled: !!selectedProfileId,
    });

    // isFetching, not isLoading: with a cached list React Query serves the previous result
    // while it refetches, so reopening the modal would show a work order list from before the
    // newest one was created. This is a selection list, so a stale entry means syncing to the
    // wrong work order — wait for the refetch instead of rendering what we already had.
    const { data: workOrders, isFetching: isLoadingWorkOrders } = useQuery({
        queryKey: ['work-orders', 'artisan-sync'],
        queryFn: () => workOrderService.getAll(),
        enabled: isSyncModalOpen,
    });

    // ─── Mutations ───
    const syncMutation = useMutation({
        mutationFn: ({ profileId, workOrderId }: { profileId: string; workOrderId: string }) =>
            artisanProfileService.syncToWorkOrder(profileId, workOrderId),
        onSuccess: () => {
            // Shared with the unlink path in CreateWorkOrderForm so the two directions of the
            // same link can never invalidate different sets of caches (AS-005).
            invalidateArtisanLinkQueries(queryClient);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: artisanProfileService.remove,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['artisan-profiles'] });
        },
    });

    const bulkDeleteMutation = useMutation({
        mutationFn: artisanProfileService.bulkRemove,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['artisan-profiles'] });
        },
    });

    return {
        profiles: profilesResponse?.data,
        totalProfiles: profilesResponse?.total ?? 0,
        isLoadingList,
        filters,
        setFilters,
        page,
        size,
        setPage,
        setSize,
        selectedProfileId,
        setSelectedProfileId,
        selectedProfile,
        isLoadingDetail,
        workOrders,
        isLoadingWorkOrders,
        syncMutation,
        deleteMutation,
        bulkDeleteMutation,
    };
}
